import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionContext } from '@nerds-at-play/contracts';
import { entryApi, EntryError, entryMessage } from './formation-api.js';

/** Session identity owns the lifetime of private panels; never persist their contents. */
export function useBoardSession() {
  const [session, setSession] = useState<SessionContext | null>(null);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState('');
  const [epoch, setEpoch] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const generation = useRef(0);
  const current = useRef<SessionContext | null>(null);
  const blocked = useRef(false);
  const checking = useRef(false);
  const accept = useCallback((value: SessionContext | null) => {
    generation.current++;
    if (JSON.stringify(current.current) !== JSON.stringify(value)) {
      setEpoch((value) => value + 1);
      current.current = value;
      setSession(value);
    }
    setChecked(true);
  }, []);
  const refresh = useCallback(async () => {
    if (blocked.current || checking.current) return;
    checking.current = true;
    const request = generation.current;
    try {
      const value = await entryApi<SessionContext>('/api/me');
      if (request !== generation.current) return;
      accept(value);
      setError('');
    } catch (e) {
      if (request !== generation.current) return;
      if (e instanceof EntryError && e.status === 401) {
        if (current.current)
          setError('Your session ended. Join again, or ask the Game Master for a replacement PIN.');
        accept(null);
      } else setError(entryMessage(e));
    } finally {
      checking.current = false;
    }
  }, [accept]);
  useEffect(() => {
    void refresh();
    const foreground = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(foreground, 5000);
    window.addEventListener('focus', foreground);
    window.addEventListener('online', foreground);
    window.addEventListener('pageshow', foreground);
    document.addEventListener('visibilitychange', foreground);
    return () => {
      generation.current++;
      clearInterval(timer);
      window.removeEventListener('focus', foreground);
      window.removeEventListener('online', foreground);
      window.removeEventListener('pageshow', foreground);
      document.removeEventListener('visibilitychange', foreground);
    };
  }, [refresh]);
  useEffect(() => {
    if (!session) return;
    const expire = () => {
      if (Date.parse(session.expiresAt) > Date.now()) return;
      accept(null);
      setError('Your session expired. Ask the Game Master about access, then join again.');
    };
    const timer = window.setTimeout(
      expire,
      Math.max(0, Math.min(2147483647, Date.parse(session.expiresAt) - Date.now())),
    );
    const interval = window.setInterval(expire, 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [session, accept]);
  async function logout() {
    blocked.current = true;
    setLoggingOut(true);
    setError('');
    accept(null);
    setEpoch((value) => value + 1);
    try {
      await entryApi('/api/session', 'DELETE');
      await entryApi('/api/formation/session', 'DELETE');
      try {
        localStorage.removeItem('nap-request-reference');
      } catch {
        /* Storage can be disabled. */
      }
      blocked.current = false;
      setLoggingOut(false);
    } catch (e) {
      setError(
        `Logout is not confirmed. Retry logout before joining another team. ${entryMessage(e)}`,
      );
      // Keep entry blocked until the server confirms both revocations.
    }
  }
  return { session, checked, error, epoch, loggingOut, logout, refresh, accept };
}
